import { inject, injectable } from '~/container'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { Distance } from '~/utility/geo/Distance'
import { PuFetchInstance } from '~/utility/http'
import { getTransformObject } from '~/shared/photos/transforms'
import { SYMBOLS } from '../inversify/symbols'
import { CampsiteDetailsRepository } from './repositories'
import {
  CampsiteDetails,
  QueryParams,
  PhotoResultUrl,
  PitchType,
  PitchTypePhoto,
  PitchTypePhotoUrl,
  PrimaryPhotos,
} from './types'

interface ResponsePhoto {
  caption: string
  url: {
    master_image: string
  }
  titleAttribution: string
}

type ResponsePrimaryPhotos = Record<string, ResponsePhoto>

@injectable()
export class AjaxCampsiteDetailsRepository
implements CampsiteDetailsRepository
{
  @inject(SYMBOLS.PuFetchInstance)
  private puFetch: PuFetchInstance

  async get(
    slug: string,
    langCode: string,
    queryParams?: QueryParams,
  ): Promise<CampsiteDetails | void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await this.puFetch.get(this.getURL(slug, langCode))
    return {
      ...response,
      ...this.mapPitchTypesToCheapestPrice(response.pitchtypes),
      id: response.id.toString(),
      attractions: {
        ...response.attractions,
        waterQuality: this.mapWaterQuality(response.attractions.waterQuality),
      },
      nearbyTransport: response.nearbyTransport || [],
      breadcrumbs: response.breadcrumbs,
      cancellationPolicyNeedsTranslation:
        !!response.cancellationPolicyNeedsTranslation,
      descriptionNeedsTranslation: !!response.descriptionNeedsTranslation,
      faq: response.faq ? this.parseFAQ(response.faq, queryParams) : undefined,
      nearbyActivitiesNeedsTranslation:
        !!response.nearbyActivitiesNeedsTranslation,
      noticesNeedsTranslation: !!response.noticesNeedsTranslation,
      pitchtypes: response.pitchtypes.map((pitchtype) => ({
        ...pitchtype,
        photos: this.mapPitchTypePhotos(pitchtype.photos),
      })),
      primaryPhoto: response.primaryPhoto
        ? {
          caption: response.primaryPhoto?.caption || '',
          url: getTransformObject<PhotoResultUrl>(
            'campsitePrimaryPhotoMaster',
            response.primaryPhoto.url.master_image,
          ),
          titleAttribution: response.primaryPhoto?.titleAttribution || '',
        }
        : null,
      primaryPhotos: this.mapPrimaryPhotos(response.primaryPhotos),
      campingArrivalTimeFrom: response.campingArrivalTimeFrom,
      campingArrivalTimeTo: response.campingArrivalTimeTo,
      campingDepartTimeFrom: response.campingDepartTimeFrom,
      onsiteArrivalTimeFrom: response.onsiteArrivalTimeFrom,
      onsiteArrivalTimeTo: response.onsiteArrivalTimeTo,
      onsiteDepartTimeFrom: response.onsiteDepartTimeFrom,
      warnings: response.covidWarnings || [],
    }
  }

  private parseFAQ(content: string, queryParams?: QueryParams) {
    const found = content.match(/href="(.*?)"/g)
    if (!found?.length) {
      return content
    }
    let newContent = content
    const params = this.getSearchParams(queryParams).toString()
    for (const match of found) {
      const path = match.replace('href=', '').replace(/"*/g, '')
      // TODO: fix using this baseUrl to rewrite internal links
      const internalUrl =
        process.env.NUXT_AXIOS_BASE_URL || 'http://pitchupcom-app:8000'
      const url = new URL(path, internalUrl)
      // const url = new URL(path, process.env.baseUrl)
      url.search = params
      const href = `href="${url.toString().replace(internalUrl || '', '')}"`
      newContent = newContent.replace(match, href)
    }
    return newContent
  }

  private getSearchParams(queryParams?: QueryParams) {
    const params = new URLSearchParams()
    if (!queryParams) {
      return params
    }
    for (const k in queryParams) {
      if (Array.isArray(queryParams[k])) {
        for (const i of Object.keys(queryParams[k])) {
          params.append(k, queryParams[k][i] || '')
        }
      } else {
        params.append(k, (queryParams[k] as string | null) || '')
      }
    }
    return params
  }

  private mapPitchTypePhotos(photos: ResponsePhoto[]): PitchTypePhoto[] {
    return photos.map((photo) => ({
      ...photo,
      url: getTransformObject<PitchTypePhotoUrl>(
        'pitchType',
        photo.url.master_image,
      ),
      titleAttribution: photo.titleAttribution || '',
    }))
  }

  private mapPitchTypesToCheapestPrice(pitchtypes: PitchType[] = []) {
    return {
      leadPriceNights:
        (pitchtypes.length && pitchtypes[0].leadPriceNights) || 1,
      leadPriceAdults:
        (pitchtypes.length && pitchtypes[0].personsIncluded) ||
        Number(process.env.defaultAdultsCount),
    }
  }

  private mapPrimaryPhotos(
    primaryPhotos: ResponsePrimaryPhotos,
  ): PrimaryPhotos {
    return Object.entries(primaryPhotos).reduce(
      (accumulator, [category, photo]) => {
        accumulator[category] = {
          ...photo,
          url: getTransformObject<PhotoResultUrl>(
            'campsitePrimaryPhotoMaster',
            photo.url.master_image,
          ),
        }
        return accumulator
      },
      {},
    )
  }

  private mapWaterQuality(waterQuality) {
    return waterQuality.map((wq) => {
      return {
        ...wq,
        distance: this.convertToDistanceObject(wq.distance),
      }
    })
  }

  private getURL(slug: string, langCode: string) {
    return getLangFallbackUrl(langCode, `/_/campsite-details/${slug}/`)
  }

  private convertToDistanceObject(distanceInMeters: number) {
    return Distance.new({ km: distanceInMeters / 1000 })
  }
}
